// ---------------------------------------------------------------------------
// Desktop (OS-level) notifications via the Web Notifications API.
//
// Two independent signals must be true for a notification to actually fire:
//   1. The browser has granted permission (Notification.permission === "granted")
//   2. The user has opted in via the bell-dropdown toggle (`enabled` pref)
//
// We separate them because permission-denied is sticky (per-origin browser
// setting), and even a granted user might want a "quiet mode" toggle.
//
// Behavior:
//   - Never fires while the current tab is visible+focused — the user is
//     already looking at the in-app bell, an OS pop-up would just be noise.
//   - Dedupes via the `tag` option, so a flurry of pushes collapses into one
//     visible pop-up.
//   - Click on the notification focuses the tab and navigates to `link`.
//
// Module-level state (`enabledPref` + listeners) is shared across every hook
// instance, so the topbar toggle and the WS dispatch see the same value.
// ---------------------------------------------------------------------------

export type DesktopPermission = "default" | "granted" | "denied" | "unsupported";

const PREF_KEY = "kms-desktop-notify";
const PREF_EVENT = "kms:desktop-notify-pref";

export function isSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export function getPermission(): DesktopPermission {
  if (!isSupported()) return "unsupported";
  return window.Notification.permission as DesktopPermission;
}

export function getEnabled(): boolean {
  if (typeof window === "undefined") return false;
  // Default OFF — desktop pop-ups are intrusive and need explicit opt-in.
  return window.localStorage.getItem(PREF_KEY) === "1";
}

export function setEnabled(on: boolean): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(PREF_KEY, on ? "1" : "0");
  // Broadcast so other tabs / other React subscribers re-read state.
  window.dispatchEvent(new CustomEvent(PREF_EVENT));
}

/** Subscribe to pref / permission changes. Returns an unsubscribe fn. */
export function subscribePrefChange(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onPref    = () => cb();
  const onStorage = (e: StorageEvent) => { if (e.key === PREF_KEY) cb(); };
  window.addEventListener(PREF_EVENT, onPref);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(PREF_EVENT, onPref);
    window.removeEventListener("storage", onStorage);
  };
}

/** Ask the browser for permission. Idempotent — re-prompts only if "default". */
export async function requestPermission(): Promise<DesktopPermission> {
  if (!isSupported()) return "unsupported";
  if (window.Notification.permission !== "default") {
    return window.Notification.permission as DesktopPermission;
  }
  try {
    const result = await window.Notification.requestPermission();
    // Permission change isn't a "storage" event, but we want listeners to
    // re-read; piggy-back on the same pref event channel.
    window.dispatchEvent(new CustomEvent(PREF_EVENT));
    return result as DesktopPermission;
  } catch {
    return "default";
  }
}

export interface NotifyOptions {
  title: string;
  body?: string;
  link?: string;
  /** Same tag collapses repeat pop-ups into one — use e.g. notification id. */
  tag?: string;
  /** Bypass the "skip when tab visible" gate (used for the test/confirm pop). */
  force?: boolean;
}

/** Fire a desktop notification if every gate is open. Silent no-op otherwise.
 *  Set `force: true` to bypass the visibility gate (used for the test ping). */
export function notify(opts: NotifyOptions): void {
  if (!isSupported()) {
    console.warn("[desktop-notify] skipped: browser doesn't support Notification API");
    return;
  }
  if (getPermission() !== "granted") {
    console.warn(`[desktop-notify] skipped: permission is "${getPermission()}"`);
    return;
  }
  if (!getEnabled()) {
    console.warn("[desktop-notify] skipped: user hasn't enabled the pref");
    return;
  }
  // Skip when the tab is *currently visible* — they can see the in-app bell.
  // (We used to also require hasFocus() but that double-gate was too strict:
  // a tab on a second monitor was still "visible" but unfocused, and the
  // pop-up never fired. Background tabs / minimised windows still pop.)
  if (!opts.force
      && typeof document !== "undefined"
      && document.visibilityState === "visible") {
    console.info("[desktop-notify] skipped: tab is in foreground");
    return;
  }
  try {
    // `renotify` re-alerts (sound + visual) when an existing tag is reused;
    // it's a real option on Chromium / Firefox but absent from the TS lib,
    // so we slip past via an inline cast. Same tag → replace the existing
    // pop-up instead of stacking.
    const n = new window.Notification(opts.title, {
      body: opts.body,
      tag: opts.tag,
      // /vite.svg is the default Vite favicon — fine as a placeholder until
      // we add a branded PNG. (Notification.icon is best-effort across OSes.)
      icon: "/vite.svg",
      ...(opts.tag ? { renotify: true } : {}),
    } as NotificationOptions & { renotify?: boolean });
    if (opts.link) {
      n.onclick = () => {
        try { window.focus(); } catch { /* noop */ }
        // Use assign so back-button still works.
        window.location.assign(opts.link!);
        n.close();
      };
    }
  } catch (err) {
    // Some browsers throw inside non-secure or iframed contexts.
    console.warn("[desktop-notify] Notification constructor threw:", err);
  }
}
